import { type KeyboardShortcut } from '@databases/react';

import {
  addFieldAfter,
  addFieldBefore,
  addEntryAfter,
  addEntryBefore,
  backspace,
  deleteEntry,
  type Keymap,
  makeKeyMapWithCommon,
  moveFieldLeft,
  moveFieldRight,
  moveEntryDown,
  moveEntryUp,
  openComments,
} from './keymaps';

export const isApple = !!navigator.platform.match(/iphone|ipad|ipod|mac/i);

export const UNDO_SHORTCUT: KeyboardShortcut[] = [
  {
    modifiers: [isApple ? 'metaKey' : 'ctrlKey'],
    code: 'KeyZ',
  },
];

export const REDO_SHORTCUT: KeyboardShortcut[] = [
  {
    modifiers: [isApple ? 'metaKey' : 'ctrlKey', 'shiftKey'],
    code: 'KeyZ',
  },
];

export const DUPLICATE_SHORTCUT: KeyboardShortcut[] = [
  {
    modifiers: ['ctrlKey'],
    code: 'KeyD',
  },
  {
    modifiers: ['metaKey'],
    code: 'KeyD',
  },
];

export const ADD_ABOVE_SHORTCUT: KeyboardShortcut[] = [
  {
    modifiers: ['ctrlKey', 'altKey'],
    code: 'ArrowUp',
  },
  {
    modifiers: ['metaKey', 'altKey'],
    code: 'ArrowUp',
  },
];

export const ADD_BELOW_SHORTCUT: KeyboardShortcut[] = [
  {
    modifiers: ['ctrlKey', 'altKey'],
    code: 'ArrowDown',
  },
  {
    modifiers: ['metaKey', 'altKey'],
    code: 'ArrowDown',
  },
];

export const MOVE_UP_SHORTCUT: KeyboardShortcut[] = [
  {
    modifiers: ['ctrlKey', 'altKey', 'shiftKey'],
    code: 'ArrowUp',
  },
  {
    modifiers: ['metaKey', 'altKey', 'shiftKey'],
    code: 'ArrowUp',
  },
];

export const MOVE_DOWN_SHORTCUT: KeyboardShortcut[] = [
  {
    modifiers: ['ctrlKey', 'altKey', 'shiftKey'],
    code: 'ArrowDown',
  },
  {
    modifiers: ['metaKey', 'altKey', 'shiftKey'],
    code: 'ArrowDown',
  },
];

export const ADD_LEFT_SHORTCUT: KeyboardShortcut[] = [
  {
    modifiers: ['ctrlKey', 'altKey'],
    code: 'ArrowLeft',
  },
  {
    modifiers: ['metaKey', 'altKey'],
    code: 'ArrowLeft',
  },
];

export const ADD_RIGHT_SHORTCUT: KeyboardShortcut[] = [
  {
    modifiers: ['ctrlKey', 'altKey'],
    code: 'ArrowRight',
  },
  {
    modifiers: ['metaKey', 'altKey'],
    code: 'ArrowRight',
  },
];

export const MOVE_LEFT_SHORTCUT: KeyboardShortcut[] = [
  {
    modifiers: ['ctrlKey', 'altKey', 'shiftKey'],
    code: 'ArrowLeft',
  },
  {
    modifiers: ['metaKey', 'altKey', 'shiftKey'],
    code: 'ArrowLeft',
  },
];

export const MOVE_RIGHT_SHORTCUT: KeyboardShortcut[] = [
  {
    modifiers: ['ctrlKey', 'altKey', 'shiftKey'],
    code: 'ArrowRight',
  },
  {
    modifiers: ['metaKey', 'altKey', 'shiftKey'],
    code: 'ArrowRight',
  },
];

export const CLEAR_SHORTCUT: KeyboardShortcut[] = [
  {
    modifiers: [],
    code: 'Backspace',
  },
];

export const DELETE_SHORTCUT: KeyboardShortcut[] = [
  {
    modifiers: ['ctrlKey'],
    code: 'Backspace',
  },
  {
    modifiers: ['metaKey'],
    code: 'Backspace',
  },
];

export const OPEN_COMMENTS: KeyboardShortcut[] = [
  {
    modifiers: ['metaKey', 'altKey'],
    code: 'KeyC',
  },
  {
    modifiers: ['ctrlKey', 'altKey'],
    code: 'KeyC',
  },
];

export const SUBMIT_SHORTCUT: KeyboardShortcut[] = [
  {
    modifiers: [],
    code: 'Enter',
  },
];

export const CLOSE_SHORTCUT: KeyboardShortcut[] = [{ modifiers: [], code: 'Escape' }];

export type Shortcuts = {
  /**
   * Display information for the keyboard shortcut.
   * Used for showing shortcuts in UI elements like tooltips, help dialogs, and menus.
   * Contains human-readable descriptions and platform-specific formatting.
   */
  keyMap: Keymap;

  /**
   * Functional definition for keyboard event detection.
   * Used for programmatically detecting when the shortcut key combination is pressed.
   * Contains the actual key codes and modifiers that trigger the shortcut action.
   */
  keyboardShortCut: KeyboardShortcut[];
};

export const SHORTCUTS = {
  ADD_ENTRY_ABOVE: {
    keyMap: addEntryBefore,
    keyboardShortCut: ADD_ABOVE_SHORTCUT,
  },
  ADD_ENTRY_BELOW: {
    keyMap: addEntryAfter,
    keyboardShortCut: ADD_BELOW_SHORTCUT,
  },
  MOVE_ENTRIES_UP: {
    keyMap: moveEntryUp,
    keyboardShortCut: MOVE_UP_SHORTCUT,
  },
  MOVE_ENTRIES_DOWN: {
    keyMap: moveEntryDown,
    keyboardShortCut: MOVE_DOWN_SHORTCUT,
  },
  ADD_FIELD_LEFT: {
    keyMap: addFieldBefore,
    keyboardShortCut: ADD_LEFT_SHORTCUT,
  },
  ADD_FIELD_RIGHT: {
    keyMap: addFieldAfter,
    keyboardShortCut: ADD_RIGHT_SHORTCUT,
  },
  MOVE_FIELDS_LEFT: {
    keyMap: moveFieldLeft,
    keyboardShortCut: MOVE_LEFT_SHORTCUT,
  },
  MOVE_FIELDS_RIGHT: {
    keyMap: moveFieldRight,
    keyboardShortCut: MOVE_RIGHT_SHORTCUT,
  },
  CLEAR: {
    keyMap: backspace,
    keyboardShortCut: CLEAR_SHORTCUT,
  },
  DELETE: {
    keyMap: deleteEntry,
    keyboardShortCut: DELETE_SHORTCUT,
  },
  DUPLICATE: {
    keyMap: makeKeyMapWithCommon('Copy', 'Mod-d'),
    keyboardShortCut: DUPLICATE_SHORTCUT,
  },
  OPEN_COMMENTS: {
    keyMap: openComments,
    keyboardShortCut: OPEN_COMMENTS,
  },
};
